import React, { useState, useEffect } from 'react';
import Xarrow, { Xwrapper } from 'react-xarrows';

const HierarchicalStructure = ({ data }) => {
  // Columns for companies, teams, departments, divisions, and positions.
  const [columns, setColumns] = useState({
    teams: { title: 'Teams', items: [] },

    companies: { title: 'Companies', items: [] },
    departments: { title: 'Departments', items: [] },
    divisions: { title: 'Divisions', items: [] },
    positions: { title: 'Positions', items: [] },
  });

  useEffect(() => {
    // Exit if no data
    if (!data || typeof data !== 'object') return;

    // Build fresh columns to avoid reusing stale arrays
    const newColumns = {
      teams: { title: 'Teams', items: [] },

      companies: { title: 'Companies', items: [] },
      departments: { title: 'Departments', items: [] },
      divisions: { title: 'Divisions', items: [] },
      positions: { title: 'Positions', items: [] },
    };

    // Process each company in `data`.
    Object.entries(data).forEach(([companyName, companyData]) => {
      if (!companyData) return;

      // 1) Create a "company" item.
      const companyId = `company-${companyData.id}`;
      const companyItem = {
        id: companyId,
        name: companyName,
        type: 'company',
        connections: [], // will connect to teams & departments
      };

      // 2) Process teams: add each team card, connect company → team
      if (Array.isArray(companyData.teams)) {
        companyData.teams.forEach(team => {
          if (team?.id && team?.name) {
            const teamId = `team-${team.id}`;
            newColumns.teams.items.push({
              id: teamId,
              name: team.name,
              type: 'team',
              connections: [companyId],
            });
          }
        });
      }

      // 3) Process departments
      if (Array.isArray(companyData.departments)) {
        companyData.departments.forEach(dept => {
          if (!dept?.id || !dept?.name) return;
          const deptId = `dept-${dept.id}`;
          const deptItem = {
            id: deptId,
            name: dept.name,
            type: 'department',
            connections: [], // will connect to divisions
          };

          // Connect company → department
          companyItem.connections.push(deptId);
          newColumns.departments.items.push(deptItem);

          // 4) Each department has an array of divisions
          if (Array.isArray(dept.divisions)) {
            dept.divisions.forEach(division => {
              if (!division?.id || !division?.name) return;
              const divisionId = `div-${division.id}`;
              const divisionItem = {
                id: divisionId,
                name: division.name,
                type: 'division',
                connections: [], // will connect to positions
              };

              // Connect department → division
              deptItem.connections.push(divisionId);
              newColumns.divisions.items.push(divisionItem);

              // 5) Each division has an array of positions
              if (Array.isArray(division.positions)) {
                division.positions.forEach(pos => {
                  if (!pos?.id || !pos?.name) return;
                  const posId = `pos-${pos.id}`;
                  const posItem = {
                    id: posId,
                    name: pos.name,
                    type: 'position',
                    connections: [],
                  };

                  // Connect division → position
                  divisionItem.connections.push(posId);
                  newColumns.positions.items.push(posItem);
                });
              }
            });
          }
        });
      }

      // Finally, add the company item to the "companies" column
      newColumns.companies.items.push(companyItem);
    });

    // Update state
    setColumns(newColumns);
  }, [data]);

  // A simple card component: "id" is used as the anchor for Xarrow
  const CardItem = ({ item }) => (
    <div
      id={item.id}
      style={{
        backgroundColor: '#fff',
        padding: '10px',
        marginBottom: '50px',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative', // helps Xarrow position the arrow
      }}
    >
      <div style={{ fontWeight: 'bold' }}>{item.name}</div>
      <div style={{ fontSize: '12px', color: '#666' }}>{item.type}</div>
    </div>
  );

  return (
    <div style={{ padding: '20px' }}>
      <Xwrapper>
        <div style={{ display: 'flex', gap: '120px', position: 'relative' }}>
          {Object.entries(columns).map(([colKey, column]) => (
            <div key={colKey} style={{ flex: 1 }}>
              <h2>{column.title}</h2>
              <div
                style={{
                  background: '#f1f1f1',
                  padding: '10px',
                  borderRadius: '4px',
                  minHeight: '300px',
                  minWidth: '100px',
                  maxWidth: '200px'
                }}
              >
                {column.items.map(item => (
                  <React.Fragment key={item.id}>
                    <CardItem item={item} />
                    {item.connections?.map(targetId => (
                      <Xarrow
                        key={`${item.id}-${targetId}`}
                        start={item.id}
                        end={targetId}
                        color="#0832DE"
                        strokeWidth={2}
                        path="smooth"
                      />
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Xwrapper>
    </div>
  );
};

export default HierarchicalStructure;
